import { useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Loader } from "lucide-react";

import { ApiError } from "@/api/fetcher";
import { getGetMeQueryKey, useLogin } from "@/api/generated";
import { Logo } from "@/components/layout/Rail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { safeRedirect } from "@/lib/redirect";

function loginError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 401) return "Wrong email or password.";
    if (e.status === 429) return "Too many sign-in attempts. Wait a few minutes, then try again.";
    return e.message;
  }
  return "Cannot reach Atlas. Check your connection.";
}

export function LoginPage() {
  const search = useSearch({ from: "/login" });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const login = useLogin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    login.mutate(
      { data: { email: email.trim(), password } },
      {
        onSuccess: (me) => {
          qc.clear();
          qc.setQueryData(getGetMeQueryKey(), me);
          navigate({ href: safeRedirect(search.redirect), replace: true });
        },
      },
    );
  };

  return (
    <div className="flex min-h-dvh items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-xl border bg-card p-6 shadow-2xl shadow-black/40">
        <div className="-mx-3 mb-4"><Logo expanded /></div>
        <h1 className="text-lg font-semibold">Sign in</h1>
        <p className="mb-5 text-sm text-muted-foreground">Network topology and performance monitoring.</p>
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {login.error && <p role="alert" className="text-sm text-destructive">{loginError(login.error)}</p>}
          <Button type="submit" disabled={login.isPending}>
            {login.isPending && <Loader className="size-4 animate-spin" />} Sign in
          </Button>
        </div>
      </form>
    </div>
  );
}
